// Notifications on this device (Oct 2026, idea 39): the hosted app's service worker receives Web Push messages from the
// platform. Off until the person presses "Turn on" on this device and the browser asks for (and gets) permission.
// iPhone / iPad: only when AVRASTRO is added to the Home Screen (Safari → Share → Add to Home Screen), iOS 16.4+.
//
// Requirement 13 (22 Oct 2026): «Turn on» could hang for ever — it awaited navigator.serviceWorker.ready, which never
// settles when no service worker is registered (a page opened from a preview, a private window that blocks it, a
// browser that refused the registration). Now the service worker is registered here when missing and every step has a
// time limit and its own error code (PushError.code → push.error.<code>, with troubleshooting text). The desktop app
// (Tauri) has no service worker and no push service: it shows native notifications while it runs (local-reminders.ts).

const fromB64u = (s: string) => { let x = s.replace(/-/g, '+').replace(/_/g, '/'); while (x.length % 4) x += '='; return Uint8Array.from(atob(x), (c) => c.charCodeAt(0)); };

export type PushSupport = 'ok' | 'unsupported' | 'ios_home_screen' | 'denied' | 'insecure' | 'desktop_app';
export function pushSupport(): PushSupport {
  if (typeof window === 'undefined') return 'unsupported';
  if ('__TAURI_INTERNALS__' in globalThis) return 'desktop_app';
  if (!window.isSecureContext) return 'insecure';
  if (!('serviceWorker' in navigator)) return 'unsupported';
  const ios = /iPhone|iPad|iPod/.test(navigator.userAgent);
  const standalone = (navigator as { standalone?: boolean }).standalone === true || window.matchMedia?.('(display-mode: standalone)').matches;
  if (!('PushManager' in window) || typeof Notification === 'undefined') return ios && !standalone ? 'ios_home_screen' : 'unsupported';
  if (Notification.permission === 'denied') return 'denied';
  return 'ok';
}

export type PushErrorCode = 'denied' | 'dismissed' | 'sw_failed' | 'sw_timeout' | 'subscribe_failed' | 'subscribe_timeout' | 'unsupported';
export class PushError extends Error {
  readonly code: PushErrorCode;
  constructor(code: PushErrorCode, detail?: string) { super(detail ? `${code}: ${detail}` : code); this.code = code; this.name = 'PushError'; }
}
/** a promise that rejects with `code` after `ms` (the step it guards may otherwise never settle) */
export function withTimeout<T>(p: Promise<T>, ms: number, code: PushErrorCode): Promise<T> {
  let t: ReturnType<typeof setTimeout>;
  return Promise.race([p, new Promise<T>((_, reject) => { t = setTimeout(() => reject(new PushError(code)), ms); })]).finally(() => clearTimeout(t));
}
export const SW_TIMEOUT_MS = 15_000;
export const SUBSCRIBE_TIMEOUT_MS = 20_000;

/** the active service worker registration: the existing one, or `sw.js` registered now; waits at most SW_TIMEOUT_MS */
export interface PushTimeouts { swMs?: number; subscribeMs?: number }
export async function ensureServiceWorker(o: PushTimeouts = {}): Promise<ServiceWorkerRegistration> {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) throw new PushError('unsupported');
  const ms = o.swMs ?? SW_TIMEOUT_MS;
  let reg: ServiceWorkerRegistration | undefined;
  try { reg = await withTimeout(navigator.serviceWorker.getRegistration(), ms, 'sw_timeout'); } catch (e) { if (e instanceof PushError) throw e; reg = undefined; }
  if (!reg) {
    try { reg = await withTimeout(navigator.serviceWorker.register('sw.js'), ms, 'sw_timeout'); } catch (e) { if (e instanceof PushError) throw e; throw new PushError('sw_failed', String((e as Error)?.message ?? e)); }
  }
  if (reg.active) return reg;
  // installing / waiting: `ready` settles once it is active (bounded here)
  return withTimeout(navigator.serviceWorker.ready, ms, 'sw_timeout');
}

/** the current subscription of this browser, if any (never asks for anything) */
export async function currentSubscription(): Promise<PushSubscription | null> {
  try {
    if (!('serviceWorker' in navigator)) return null;
    const reg = await withTimeout(navigator.serviceWorker.getRegistration(), 5_000, 'sw_timeout');
    return (await reg?.pushManager.getSubscription()) ?? null;
  } catch { return null; }
}
/** asks the browser for permission and subscribes with the platform's key. Throws PushError (never hangs). */
export async function subscribePush(vapidPublicKey: string, o: PushTimeouts = {}): Promise<PushSubscriptionJSON> {
  if (typeof Notification === 'undefined') throw new PushError('unsupported');
  const perm = Notification.permission === 'granted' ? 'granted' : await Notification.requestPermission();
  if (perm === 'denied') throw new PushError('denied');
  if (perm !== 'granted') throw new PushError('dismissed');
  const reg = await ensureServiceWorker(o);
  try {
    const existing = await reg.pushManager.getSubscription();
    const sub = existing ?? await withTimeout(reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: fromB64u(vapidPublicKey) }), o.subscribeMs ?? SUBSCRIBE_TIMEOUT_MS, 'subscribe_timeout');
    return sub.toJSON();
  } catch (e) {
    if (e instanceof PushError) throw e;
    throw new PushError('subscribe_failed', String((e as Error)?.message ?? e));
  }
}
/** the same short key the platform shows for each device (sha-256 of the push address, 16 hex) */
export async function endpointKey(endpoint: string): Promise<string> {
  const h = new Uint8Array(await crypto.subtle.digest('SHA-256', new TextEncoder().encode(endpoint)));
  return [...h.slice(0, 8)].map((b) => b.toString(16).padStart(2, '0')).join('');
}
export async function unsubscribePush(): Promise<void> { const s = await currentSubscription(); await s?.unsubscribe().catch(() => undefined); }
/** this device's IANA time zone (quiet hours and reminder times are its wall clock) */
export function deviceTimeZone(): string { try { return Intl.DateTimeFormat().resolvedOptions().timeZone || 'Europe/Nicosia'; } catch { return 'Europe/Nicosia'; } }
